import { useState } from "react";
import {
  AlertTriangle,
  Ban,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const Count = ({ label, value, tone }) => (
  <div className="bg-card p-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-data mt-1 text-xl font-bold" style={tone ? { color: tone } : undefined}>
      {value}
    </div>
  </div>
);

const Fold = ({ title, count, tone, children, testId }) => {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div className="border border-border">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold transition-colors duration-200 hover:bg-muted"
      >
        <span style={tone ? { color: tone } : undefined}>
          {title} ({count})
        </span>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open && <ul className="max-h-72 overflow-y-auto border-t border-border">{children}</ul>}
    </div>
  );
};

const Row = ({ children }) => (
  <li className="border-b border-border px-3 py-2 text-xs last:border-b-0">{children}</li>
);

export const TemplateDiff = ({ staged, picked, setPicked, dropMissing, setDropMissing,
                               replaceAll, setReplaceAll, firstFill = [], tvOnly = false,
                               applying, onApply, onDiscard }) => {
  const diff = staged.template;
  const problems = diff.problem_rows || 0;
  return (
    <div className="mt-8 space-y-6" data-testid="staged-template">
      <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-card p-4">
        <div>
          <div className="font-display text-base font-bold">{staged.file_name}</div>
          <div className="text-xs text-muted-foreground">
            Bulk update workbook · {staged.size_kb} KB · {diff.total_rows} row(s) across{" "}
            {diff.units.length} unit sheet(s) · read by {staged.uploaded_by}. Nothing is saved yet.
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-10 rounded-none"
            data-testid="discard-upload"
            onClick={onDiscard}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Discard
          </Button>
          <Button
            className="h-10 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            disabled={applying || !picked.length}
            data-testid="apply-upload"
            onClick={onApply}
          >
            {applying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            Apply to {picked.length} unit(s)
          </Button>
        </div>
      </div>

      {problems > 0 && (
        <div
          className="flex items-start gap-2 border border-[hsl(var(--watch))] bg-card p-3 text-xs"
          data-testid="template-problems-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--watch))]" />
          <span>
            <b>{problems} row(s) will come in with something still to fill</b> — a blank the portal
            needs, or a word that is not in its list. The row still goes in; what is missing is
            marked on the use case as <b>Needs a fix</b> so it can be completed on the portal.
          </span>
        </div>
      )}

      {staged.blocked?.map((b) => (
        <p key={b.unit} className="border border-border bg-card p-3 text-xs text-muted-foreground">
          {b.unit} sheet ignored — {b.reason}
        </p>
      ))}

      {tvOnly ? (
        <div className="border border-border bg-card p-4 text-xs" data-testid="tv-only-note">
          <b>Only the Pravartanam-TV rows of this workbook are read.</b> Every other row is left out
          and named above, and what does come in waits for the Pravartanam Partner Manager before
          the register carries it.
        </div>
      ) : (
        <label className="flex items-start gap-3 border border-border bg-card p-4 text-xs">
          <Checkbox
            className="mt-0.5"
            checked={replaceAll}
            data-testid="replace-all"
            onCheckedChange={(v) => setReplaceAll(!!v)}
          />
          <span>
            <b>This workbook is the unit's complete register</b> — every use case takes the
            workbook's figures, a cell left blank clears what the portal held, a row missing from the
            file is dropped, and every row is stamped with today's date, who entered it and who
            approved it. Leave it unticked to have only the cells that differ corrected.
            {firstFill.length > 0 && (
              <span className="mt-1 block text-muted-foreground">
                {firstFill.join(", ")} {firstFill.length === 1 ? "has" : "have"} not had a workbook
                in yet, so this is {firstFill.length === 1 ? "its" : "their"} first fill — it is
                ticked for you.
              </span>
            )}
          </span>
        </label>
      )}

      <label className="flex items-start gap-3 border border-border bg-card p-4 text-xs">
        <Checkbox
          className="mt-0.5"
          checked={dropMissing}
          data-testid="drop-missing"
          onCheckedChange={(v) => setDropMissing(!!v)}
        />
        <span>
          <b>Treat a row that has been deleted from the workbook as "Dropped"</b> — the use case
          keeps its code, its history and its readings, and is marked Dropped with the reason "row
          removed from this workbook". Untick this to leave such use cases exactly as they are.
        </span>
      </label>

      {diff.units.map((u) => (
        <div key={u.unit} className="border border-border bg-card" data-testid={`diff-${u.unit}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
            <label className="flex items-start gap-3">
              <Checkbox
                className="mt-1"
                checked={picked.includes(u.unit)}
                disabled={u.blocked}
                data-testid={`pick-${u.unit}`}
                onCheckedChange={(v) =>
                  setPicked(v ? [...picked, u.unit] : picked.filter((x) => x !== u.unit))
                }
              />
              <span>
                <span className="font-display text-lg font-bold">{u.unit}</span>
                <div className="font-data mt-1 text-xs">
                  As on {u.as_on || "—"} · filled by {u.filled_by || "not stated"}
                  {u.email ? ` (${u.email})` : ""}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {u.in_file} row(s) in the file · {u.in_portal} use case(s) in the portal
                </div>
                {(u.columns_missing || []).length > 0 && (
                  <div
                    className="mt-1 font-data text-[10px] text-[hsl(var(--watch))]"
                    data-testid={`columns-missing-${u.unit}`}
                  >
                    not found in this sheet, so left untouched: {u.columns_missing.join(", ")}
                  </div>
                )}
              </span>
            </label>
            {u.blocked && (
              <span className="text-[11px] text-muted-foreground">
                Not your unit — this sheet will be ignored.
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-6">
            <Count label="New" value={u.added.length} tone="hsl(var(--healthy))" />
            <Count label="Updated" value={u.changed.length} tone="hsl(var(--watch))" />
            <Count label="To be dropped" value={u.dropped.length} tone="hsl(var(--at-risk))" />
            <Count label="Needs a fix" value={u.problems.length} tone="hsl(var(--watch))" />
            <Count label="Left out" value={u.refused.length} />
            <Count label="Unchanged" value={u.unchanged} />
          </div>

          <div className="space-y-2 p-4">
            <Fold
              testId={`fold-added-${u.unit}`}
              title="New use cases the portal will create"
              count={u.added.length}
              tone="hsl(var(--healthy))"
            >
              {u.added.map((a, i) => (
                <Row key={i}>
                  <span className="flex items-start gap-2">
                    <Plus className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--healthy))]" />
                    <span>
                      <b>{a.title}</b>
                      <span className="ml-2 text-muted-foreground">
                        row {a.row} · {a.status}
                        {a.term ? ` · ${a.term}` : ""}
                        {a.project_type ? ` · ${a.project_type}` : ""}
                        {a.code_in_file ? ` · code "${a.code_in_file}" in the file — the portal will give it its own` : ""}
                      </span>
                      {(a.gaps || []).length > 0 && (
                        <div className="text-[11px] text-[hsl(var(--watch))]">
                          to fill on the portal: {a.gaps.map((g) => g.label).join(", ")}
                        </div>
                      )}
                    </span>
                  </span>
                </Row>
              ))}
            </Fold>

            <Fold
              testId={`fold-changed-${u.unit}`}
              title="Use cases that will be updated"
              count={u.changed.length}
              tone="hsl(var(--watch))"
            >
              {u.changed.map((c) => (
                <Row key={c.code}>
                  <div className="flex items-start gap-2">
                    <Pencil className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--watch))]" />
                    <div>
                      <span className="font-data">{c.code}</span> <b>{c.title}</b>
                      <ul className="mt-1 space-y-0.5">
                        {c.fields.map((f) => (
                          <li key={f.field} className="text-[11px] text-muted-foreground">
                            {f.label}: <s>{String(f.from ?? "—")}</s> →{" "}
                            <b className="text-foreground">{String(f.to ?? "—")}</b>
                          </li>
                        ))}
                      </ul>
                      {(c.gaps || []).length > 0 && (
                        <div className="text-[11px] text-[hsl(var(--watch))]">
                          to fill on the portal: {c.gaps.map((g) => g.label).join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                </Row>
              ))}
            </Fold>

            <Fold
              testId={`fold-problems-${u.unit}`}
              title="Rows coming in with something still to fill on the portal"
              count={u.problems.length}
              tone="hsl(var(--watch))"
            >
              {u.problems.map((p, i) => (
                <Row key={i}>
                  <span className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--watch))]" />
                    <span>
                      <span className="font-data">{p.code || "new row"}</span>{" "}
                      <b>{p.title || "(no name)"}</b>{" "}
                      <span className="text-muted-foreground">row {p.row}</span>
                      <div className="text-[11px] text-[hsl(var(--watch))]">
                        {p.issues.join(" · ")}
                      </div>
                    </span>
                  </span>
                </Row>
              ))}
            </Fold>

            <Fold
              testId={`fold-dropped-${u.unit}`}
              title="Use cases missing from the workbook"
              count={u.dropped.length}
              tone="hsl(var(--at-risk))"
            >
              {u.dropped.map((d) => (
                <Row key={d.code}>
                  <span className="font-data">{d.code}</span> <b>{d.title}</b>
                  <span className="ml-2 text-muted-foreground">
                    presently {d.status} — will be marked Dropped
                  </span>
                </Row>
              ))}
            </Fold>

            <Fold
              testId={`fold-refused-${u.unit}`}
              title="Rows left out of this upload"
              count={u.refused.length}
            >
              {u.refused.map((r, i) => (
                <Row key={i}>
                  <span className="flex items-start gap-2">
                    <Ban className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                    <span>
                      <span className="font-data">{r.code || "row " + r.row}</span>{" "}
                      <b>{r.title || ""}</b>
                      <div className="text-[11px] text-muted-foreground">{r.reason}</div>
                    </span>
                  </span>
                </Row>
              ))}
            </Fold>
          </div>
        </div>
      ))}
    </div>
  );
};
